/**
 * `/sign-up` — Tarii's own account-creation page (3 Oct 2026), built on
 * Clerk's headless `useSignUp()` hook. Clerk keeps doing the work; the
 * prebuilt Clerk card is one release away behind `?classic=1` (see
 * `SignUpGate` in `App.tsx`).
 *
 * Page protocol (founder, 21 Sep):
 *   - Why the user comes: they were invited to a team, or they are
 *     setting one up — often straight from an emailed invite, often on a
 *     phone — and they need an account before anything else.
 *   - What they see first: the Tarii mark, one line, the create-or-join
 *     strip (#1720, unchanged), Continue with Google, then email and
 *     password.
 *   - What they adjust immediately: their email (or Google). The name
 *     fields are optional and sit last; "Sign in" is a quiet link.
 *
 * Flow (Clerk legacy/Core 2 custom-flow API): `signUp.create({ email,
 * password, names })` → status `missing_requirements` with the email
 * unverified → `prepareEmailAddressVerification` (email_code) → code →
 * `attemptEmailAddressVerification` → `complete` → `setActive`. Any
 * other missing field Clerk reports is collected on a details step
 * (`signUp.update`). `/sign-up/continue` resumes an attempt Clerk left on
 * the client — the Google callback's `continueSignUpUrl`.
 *
 * Bot protection: the instance runs Clerk's "smart" CAPTCHA on sign-up,
 * so the page always carries `<CaptchaSlot/>` (the `#clerk-captcha`
 * element Clerk's custom-flow guide requires).
 *
 * After `setActive` the page does not navigate: `SignUpGate` sees the
 * session and sends the person on (pending invite → accept; otherwise
 * `/`, where the stashed create-or-join intent is honoured).
 */
import { useClerk, useSignUp } from "@clerk/clerk-react";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  AUTH_LEAD_LINE,
  AlreadySignedIn,
  AuthAlert,
  AuthField,
  AuthFrame,
  AuthNotice,
  CaptchaSlot,
  GoogleButton,
  OrDivider,
  QuietAction,
  SubmitButton,
  quietLinkClass,
} from "./AuthFrame";
import { type AuthErrorFlow, type FriendlyAuthError, describeClerkError } from "./clerkErrors";

type SignUpResource = NonNullable<ReturnType<typeof useSignUp>["signUp"]>;
type SignUpField = SignUpResource["missingFields"][number];

type Step =
  | { kind: "start" }
  | { kind: "verify"; sentTo: string }
  | { kind: "details"; fields: SignUpField[] }
  | { kind: "signed-in" };

type Busy = null | "google" | "create" | "verify" | "resend" | "details" | "finishing";

/** Fields the details step knows how to ask for. */
const DETAIL_FIELDS: ReadonlySet<string> = new Set([
  "first_name",
  "last_name",
  "username",
  "password",
  "email_address",
  "legal_accepted",
]);

export function SignUpPage({
  intentStrip,
  resume = false,
}: {
  /** The #1720 create-or-join strip, rendered above the form on the first step. */
  intentStrip?: ReactNode;
  /** Mounted at `/sign-up/continue`: pick up the attempt on the client. */
  resume?: boolean;
}) {
  const { isLoaded, signUp, setActive } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { email?: unknown } | null)?.email;

  const [step, setStep] = useState<Step>(() =>
    clerk.isSignedIn ? { kind: "signed-in" } : { kind: "start" },
  );
  const [email, setEmail] = useState(typeof prefill === "string" ? prefill : "");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<FriendlyAuthError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  const ready = isLoaded && Boolean(signUp) && Boolean(setActive);
  const locked = busy !== null;

  function go(next: Step) {
    setStep(next);
    setCode("");
    setNotice(null);
  }

  function fail(err: unknown, flow: AuthErrorFlow) {
    const friendly = describeClerkError(err, flow);
    if (friendly.action === "signed-in") {
      go({ kind: "signed-in" });
      setError(null);
      return;
    }
    setError(friendly);
  }

  async function finish(res: SignUpResource) {
    if (!setActive) return;
    setBusy("finishing");
    await setActive({
      session: res.createdSessionId,
      navigate: async ({ session }) => {
        if (session?.currentTask) await clerk.redirectToTasks();
      },
    });
  }

  /** Route any sign-up state to the step that finishes it. */
  async function advance(res: SignUpResource) {
    if (res.status === "complete") {
      await finish(res);
      return;
    }
    if (res.status === "missing_requirements") {
      if (res.unverifiedFields.includes("email_address")) {
        await res.prepareEmailAddressVerification({ strategy: "email_code" });
        go({ kind: "verify", sentTo: res.emailAddress || email.trim() });
        return;
      }
      if (res.missingFields.length > 0) {
        go({ kind: "details", fields: res.missingFields });
        return;
      }
    }
    go({ kind: "start" });
    setError({ message: "This sign-up timed out. Start again from the top.", code: null });
  }

  async function run(
    kind: Exclude<Busy, null>,
    flow: AuthErrorFlow,
    task: () => Promise<void>,
    hold = false,
  ) {
    setBusy(kind);
    setError(null);
    setNotice(null);
    let ok = false;
    try {
      await task();
      ok = true;
    } catch (err) {
      fail(err, flow);
    } finally {
      setBusy((b) => (ok && (b === "finishing" || hold) ? b : null));
    }
  }

  // `/sign-up/continue`: resume the attempt Clerk left on the client.
  // Fresh `/sign-up`: show a Google error the callback sent back here.
  const mounted = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once, when Clerk is ready.
  useEffect(() => {
    if (!ready || !signUp || mounted.current) return;
    mounted.current = true;
    if (step.kind === "signed-in") return;
    if (resume) {
      if (signUp.id && signUp.status === "missing_requirements") {
        if (signUp.emailAddress) setEmail(signUp.emailAddress);
        void run("resend", "sign-up", () => advance(signUp));
      } else {
        navigate("/sign-up", { replace: true });
      }
      return;
    }
    const oauthError = signUp.verifications?.externalAccount?.error;
    if (oauthError) fail({ errors: [oauthError] }, "sign-up");
  }, [ready]);

  useEffect(() => {
    if (step.kind === "verify") codeRef.current?.focus();
  }, [step]);

  // ── handlers ──────────────────────────────────────────────────────

  function onGoogle() {
    if (!signUp) return;
    void run(
      "google",
      "sign-up",
      () =>
        signUp.authenticateWithRedirect({
          strategy: "oauth_google",
          redirectUrl: "/sso-callback",
          redirectUrlComplete: "/",
        }),
      true,
    );
  }

  function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!signUp) return;
    if (!email.trim()) {
      setError({ message: "Enter your email address.", code: "form_param_nil" });
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      setError({ message: "Choose a password.", code: "form_param_nil" });
      passwordRef.current?.focus();
      return;
    }
    void run("create", "sign-up", async () => {
      const res = await signUp.create({
        emailAddress: email.trim(),
        password,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      });
      await advance(res);
    });
  }

  function verify(value: string) {
    if (!signUp) return;
    void run("verify", "code", async () => {
      const res = await signUp.attemptEmailAddressVerification({ code: value });
      await advance(res);
    });
  }

  function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    if (code.length < 6) {
      setError({ message: "Enter the 6-digit code from the email.", code: "form_param_nil" });
      codeRef.current?.focus();
      return;
    }
    verify(code);
  }

  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6 && !locked) verify(digits);
  }

  function onResend() {
    if (!signUp) return;
    void run("resend", "code", async () => {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setCode("");
      setNotice("We sent a new code. It replaces the one before it.");
    });
  }

  function onDetails(e: FormEvent) {
    e.preventDefault();
    if (!signUp || step.kind !== "details") return;
    const asks = new Set<string>(step.fields);
    void run("details", "sign-up", async () => {
      const res = await signUp.update({
        ...(asks.has("first_name") ? { firstName: firstName.trim() } : {}),
        ...(asks.has("last_name") ? { lastName: lastName.trim() } : {}),
        ...(asks.has("username") ? { username: username.trim() } : {}),
        ...(asks.has("password") ? { password } : {}),
        ...(asks.has("email_address") ? { emailAddress: email.trim() } : {}),
        ...(asks.has("legal_accepted") ? { legalAccepted } : {}),
      });
      await advance(res);
    });
  }

  function backToStart() {
    go({ kind: "start" });
    setError(null);
  }

  function errorActions(err: FriendlyAuthError) {
    if (err.action === "sign-in") {
      return (
        <Link to="/sign-in" state={{ email: email.trim() }} className={quietLinkClass}>
          Sign in instead
        </Link>
      );
    }
    if (err.action === "resend-code") {
      return (
        <QuietAction onClick={onResend} disabled={locked}>
          Send a new code
        </QuietAction>
      );
    }
    return undefined;
  }

  const alert = error ? <AuthAlert error={error} actions={errorActions(error)} /> : null;

  // ── steps ─────────────────────────────────────────────────────────

  let title: string;
  let lead: ReactNode;
  let body: ReactNode;
  let footer: ReactNode = null;

  if (step.kind === "signed-in") {
    title = "You're already signed in";
    lead = "This browser is signed in to Tarii.";
    body = <AlreadySignedIn />;
  } else if (step.kind === "verify") {
    title = "Check your email";
    lead = (
      <>
        We sent a 6-digit code to <span className="font-medium text-fg">{step.sentTo}</span>. Enter
        it to finish creating your account.
      </>
    );
    body = (
      <>
        <form onSubmit={onCodeSubmit} noValidate className="flex flex-col gap-4">
          <AuthField
            ref={codeRef}
            id="sign-up-code"
            label="Code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            disabled={locked}
            className="auth-code-input font-mono"
          />
          {alert}
          {notice ? <AuthNotice>{notice}</AuthNotice> : null}
          <SubmitButton
            busy={busy === "verify" || busy === "finishing"}
            busyLabel="Creating your account…"
            disabled={!ready || locked}
          >
            Continue
          </SubmitButton>
        </form>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
          <QuietAction onClick={onResend} disabled={locked}>
            {busy === "resend" ? "Sending…" : "Send a new code"}
          </QuietAction>
          <QuietAction onClick={backToStart} disabled={locked}>
            Use a different email
          </QuietAction>
        </div>
      </>
    );
  } else if (step.kind === "details") {
    const asks = new Set<string>(step.fields);
    const unsupported = step.fields.filter((f) => !DETAIL_FIELDS.has(f));
    title = "A few more details";
    lead = "Your account needs these before it's ready.";
    body = unsupported.length ? (
      <AuthAlert
        error={{
          message:
            "Your account needs a detail this page can't collect yet. Finish on the classic sign-up page.",
          code: "unsupported_field",
        }}
        actions={
          <a href="/sign-up/continue?classic=1" className={quietLinkClass}>
            Open the classic page
          </a>
        }
      />
    ) : (
      <form onSubmit={onDetails} noValidate className="flex flex-col gap-4">
        {asks.has("email_address") ? (
          <AuthField
            id="sign-up-details-email"
            label="Email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={locked}
          />
        ) : null}
        {asks.has("first_name") ? (
          <AuthField
            id="sign-up-details-first-name"
            label="First name"
            name="given-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={locked}
          />
        ) : null}
        {asks.has("last_name") ? (
          <AuthField
            id="sign-up-details-last-name"
            label="Last name"
            name="family-name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={locked}
          />
        ) : null}
        {asks.has("username") ? (
          <AuthField
            id="sign-up-details-username"
            label="Username"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={locked}
          />
        ) : null}
        {asks.has("password") ? (
          <AuthField
            id="sign-up-details-password"
            label="Password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={locked}
          />
        ) : null}
        {asks.has("legal_accepted") ? (
          <label className="flex items-start gap-3 text-fg text-sm">
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(e) => setLegalAccepted(e.target.checked)}
              disabled={locked}
              className="mt-0.5 h-5 w-5 accent-[rgb(var(--color-accent))]"
            />
            I agree to Tarii's terms of service and privacy policy.
          </label>
        ) : null}
        {alert}
        <SubmitButton
          busy={busy === "details" || busy === "finishing"}
          busyLabel="Saving…"
          disabled={!ready || locked}
        >
          Continue
        </SubmitButton>
      </form>
    );
  } else {
    title = "Create your Tarii account";
    lead = AUTH_LEAD_LINE;
    footer = (
      <>
        Already have an account?{" "}
        <Link to="/sign-in" state={{ email: email.trim() }} className={quietLinkClass}>
          Sign in
        </Link>
      </>
    );
    body = (
      <>
        {intentStrip ? <div className="mb-6">{intentStrip}</div> : null}
        <GoogleButton onClick={onGoogle} busy={busy === "google"} disabled={!ready || locked} />
        <OrDivider />
        <form onSubmit={onCreate} noValidate className="flex flex-col gap-4">
          <AuthField
            ref={emailRef}
            id="sign-up-email"
            label="Email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={locked}
          />
          <div className="flex flex-col gap-1.5">
            <AuthField
              ref={passwordRef}
              id="sign-up-password"
              label="Password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              aria-describedby="sign-up-password-hint"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={locked}
            />
            <p id="sign-up-password-hint" className="text-fg-subtle text-xs">
              At least 8 characters.
            </p>
          </div>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-fg text-sm font-medium">
              Your name <span className="font-normal text-fg-subtle">(optional)</span>
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <AuthField
                id="sign-up-first-name"
                label="First"
                name="given-name"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={locked}
              />
              <AuthField
                id="sign-up-last-name"
                label="Last"
                name="family-name"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={locked}
              />
            </div>
          </fieldset>
          {alert}
          <SubmitButton
            busy={busy === "create"}
            busyLabel="Creating your account…"
            disabled={!ready || locked}
          >
            Create account
          </SubmitButton>
        </form>
      </>
    );
  }

  return (
    <AuthFrame title={title} lead={lead} footer={footer}>
      {body}
      {/* One captcha slot for every step (see CaptchaSlot). */}
      <CaptchaSlot />
    </AuthFrame>
  );
}
